import React, { useEffect, useRef } from 'react';
type Star = {
  x: number;
  y: number;
  z: number;
  r: number;
  twinkle: number;
  hue: string;
};
type Shooter = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
};
const STAR_COLORS = ['#ffffff', '#cdd6ff', '#a481ff', '#37e5ff', '#c86bff'];
type StarfieldProps = {
  className?: string;
};
export function Starfield({
  className = 'pointer-events-none fixed inset-0 z-0 h-full w-full'
}: StarfieldProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let width = 0;
    let height = 0;
    let stars: Star[] = [];
    let shooters: Shooter[] = [];
    let frame = 0;
    let raf = 0;
    let scrollY = window.scrollY;
    let lastBuildW = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Match the canvas BACKING STORE to its CSS size. Cheap, and star positions
    // are left untouched — so a viewport height change (mobile address bar
    // showing/hiding on scroll) just re-fits the buffer instead of regenerating
    // the field. Only touches canvas.width/height when they actually differ, so
    // it doesn't clear + flicker on no-op resize events.
    const syncSize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      const bw = Math.max(1, Math.round(width * dpr));
      const bh = Math.max(1, Math.round(height * dpr));
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw;
        canvas.height = bh;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    };

    // Full (re)generation of the star field. Random positions live here, so this
    // must ONLY run on first mount and on a real WIDTH change — never on every
    // resize event (that was the old bug: the whole field jumped on mobile
    // scroll and on window drags because build() ran on each resize tick).
    const build = () => {
      syncSize();
      lastBuildW = width;
      const count = Math.min(260, Math.round(width * height / 7000));
      stars = Array.from({
        length: count
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        z: 0.3 + Math.random() * 0.7,
        r: 0.3 + Math.random() * 1.3,
        twinkle: Math.random() * Math.PI * 2,
        hue: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)]
      }));
      // DEEP FIELD (Ryan, Aug 29): a dense second population of distant stars —
      // low z = dimmer, slower drift, slower twinkle; r compensates z so they
      // stay visible ~sub-pixel dots. Roughly 2× the near-field count.
      const deepCount = Math.min(520, Math.round(width * height / 3200));
      stars = stars.concat(Array.from({
        length: deepCount
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        z: 0.08 + Math.random() * 0.22,
        r: 1.2 + Math.random() * 1.4,
        twinkle: Math.random() * Math.PI * 2,
        hue: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)]
      })));
    };
    const spawnShooter = () => {
      const fromLeft = Math.random() > 0.4;
      const speed = 5 + Math.random() * 4;
      shooters.push({
        x: fromLeft ? -60 : width * Math.random(),
        y: Math.random() * height * 0.6,
        vx: fromLeft ? speed : -speed,
        vy: speed * (0.35 + Math.random() * 0.3),
        life: 0,
        max: 70 + Math.random() * 40
      });
    };
    const draw = () => {
      frame += 1;
      ctx.clearRect(0, 0, width, height);
      const drift = scrollY * 0.06;
      for (const s of stars) {
        const y = (s.y - drift * s.z) % height;
        const yy = y < 0 ? y + height : y;
        const alpha = 0.35 + 0.65 * Math.abs(Math.sin(s.twinkle + frame * 0.012 * s.z));
        ctx.globalAlpha = alpha * (0.45 + s.z * 0.55);
        ctx.fillStyle = s.hue;
        ctx.beginPath();
        ctx.arc(s.x, yy, s.r * s.z, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (!reduced && frame % 220 === 0 && shooters.length < 2) spawnShooter();
      shooters = shooters.filter(sh => sh.life < sh.max);
      for (const sh of shooters) {
        sh.life += 1;
        sh.x += sh.vx;
        sh.y += sh.vy;
        const fade = 1 - sh.life / sh.max;
        const tailX = sh.x - sh.vx * 14;
        const tailY = sh.y - sh.vy * 14;
        const grad = ctx.createLinearGradient(sh.x, sh.y, tailX, tailY);
        grad.addColorStop(0, `rgba(255,255,255,${0.9 * fade})`);
        grad.addColorStop(0.4, `rgba(55,229,255,${0.45 * fade})`);
        grad.addColorStop(1, 'rgba(200,107,255,0)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(sh.x, sh.y);
        ctx.lineTo(tailX, tailY);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    const onScroll = () => {
      scrollY = window.scrollY;
    };

    // Debounced resize: always re-fit the buffer, but only REBUILD the field
    // when the width genuinely changed (orientation flip, desktop drag settle).
    // Height-only changes (the mobile URL bar) never regenerate stars, so the
    // field no longer glitches on scroll or resize.
    let resizeTimer = 0;
    const onResize = () => {
      syncSize();
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (Math.abs(canvas.clientWidth - lastBuildW) > 1) build();
      }, 160);
    };
    build();
    draw();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  return <canvas ref={canvasRef} aria-hidden="true" className={className} data-id="element-346" data-mp="axlau91pd9g4" />;
}